import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useResendVerification } from "../../../../hooks/authV2/useResendVerification";
import "./EmailVerification.css";

const resendSchema = Yup.object().shape({
  email: Yup.string()
    .email("Please enter a valid email address")
    .required("Email is required"),
});

const Badge = ({ variant }) => (
  <div className="ev-badge-wrap">
    <div className={`ev-badge-circle ${variant}`}>
      <span className="ev-badge-checkmark">
        {variant === "success" && (
          <svg viewBox="0 0 24 24">
            <polyline points="6 12 10 16 18 8" />
          </svg>
        )}
        {variant === "failed" && (
          <svg viewBox="0 0 24 24">
            <line x1="8" y1="8" x2="16" y2="16" />
            <line x1="16" y1="8" x2="8" y2="16" />
          </svg>
        )}
      </span>
    </div>
  </div>
);

export const EmailVerification = () => {
  const [status, setStatus] = useState("pending"); // 'pending' | 'success' | 'failed'
  const [countdown, setCountdown] = useState(10);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = searchParams.get("token");

  const {
    loading: resendLoading,
    message: resendMessage,
    setMessage: setResendMessage,
    resendVerification,
  } = useResendVerification();

  // Verify email on mount
  useEffect(() => {
    const verifyEmail = async () => {
      try {
        const response = await fetch(`${apiUrl}/auth/email/verify`, {
          method: "POST",
          body: JSON.stringify({ token }),
          headers: { "Content-Type": "application/json" },
        });

        if (response.ok) {
          setStatus("success");
        } else {
          setStatus("failed");
        }
      } catch (error) {
        setStatus("failed");
      }
    };

    if (token) {
      verifyEmail();
    } else {
      setStatus("failed");
    }
  }, []);

  // Countdown redirect on success
  useEffect(() => {
    if (status === "success") {
      const interval = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);

      const timeout = setTimeout(() => {
        navigate("/");
      }, 10000);

      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    }
  }, [status, navigate]);


  /* ─── Resend form handler ──────────────────────────────── */
  const handleResend = (values) => {
    resendVerification({ email: values.email });
  };

  /* ─── Pending state ────────────────────────────────────── */
  if (status === "pending") {
    return (
      <div className="ev-page">
        <div className="ev-container">
          <div className="ev-spinner-wrap">
            <div className="ev-spinner" />
          </div>

          <h1 className="ev-title">
            <span className="ev-highlight-pending">Verifying</span> Your Email
          </h1>
          <p className="ev-subtitle">
            Please wait while we verify your email address. This will only take a
            moment…
          </p>
        </div>
      </div>
    );
  }

  /* ─── Success state ────────────────────────────────────── */
  if (status === "success") {
    return (
      <div className="ev-page">
        <div className="ev-container">
          <Badge variant="success" />

          <h1 className="ev-title">
            Email <span className="ev-highlight-success">Verified!</span>
          </h1>
          <p className="ev-subtitle">
            Your email has been verified successfully. You can now log in to your
            account and start exploring.
          </p>

          <p className="ev-redirect-text">
            Redirecting to home in{" "}
            <span className="ev-countdown">{countdown}</span> seconds…
          </p>

          <div className="ev-actions">
            <Link to="/" className="ev-btn ev-btn-primary">
              Go to Home →
            </Link>
          </div>
        </div>
      </div>
    );
  }

  /* ─── Failed state ─────────────────────────────────────── */
  return (
    <div className="ev-page">
      <div className="ev-container">
        <Badge variant="failed" />

        <h1 className="ev-title">
          Verification <span className="ev-highlight-failed">Failed</span>
        </h1>
        <p className="ev-subtitle">
          We couldn't verify your email. The verification link may have expired
          or is invalid. You can request a new verification email below.
        </p>

        {/* Resend verification form */}
        <div className="ev-resend-section">
          <p className="ev-resend-title">Resend Verification Email</p>

          {resendMessage.text && (
            <div className={`ev-msg ev-msg--${resendMessage.type}`}>
              {resendMessage.text}
            </div>
          )}

          <Formik
            initialValues={{ email: "" }}
            validationSchema={resendSchema}
            onSubmit={handleResend}
          >
            {({ errors, touched }) => (
              <Form>
                <div className="ev-resend-form-group">
                  <label htmlFor="ev-email" className="ev-resend-label">
                    Email Address
                  </label>
                  <Field
                    id="ev-email"
                    name="email"
                    type="email"
                    placeholder="Enter your registered email"
                    className={`ev-resend-input ${errors.email && touched.email ? "is-invalid" : ""
                      }`}
                  />
                  <ErrorMessage
                    name="email"
                    component="div"
                    className="ev-resend-error"
                  />
                </div>

                <button
                  type="submit"
                  className="ev-resend-btn"
                  disabled={resendLoading}
                >
                  {resendLoading ? "Sending…" : "Resend Verification Email"}
                </button>
              </Form>
            )}
          </Formik>
        </div>

        <div className="ev-actions">
          <Link to="/" className="ev-btn ev-btn-outline">
            Go to Home
          </Link>
        </div>
      </div>
    </div>
  );
};
